import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';

const ProviderHealth = z.object({
  provider: z.string(),
  model: z.string(),
  api_key_present: z.boolean(),
  api_key_hint: z.string().nullable(),
  status: z.enum(['ok', 'degraded', 'down', 'idle']),
  p50_ms: z.number().nullable(),
  p99_ms: z.number().nullable(),
  error_rate: z.number(),
  events_count: z.number(),
  last_failure: z.object({
    ts: z.string(), error_class: z.string(), message: z.string(),
  }).nullable(),
});

export const VoiceConfigSnapshotSchema = z.object({
  stt: z.object({ primary: ProviderHealth, fallback: ProviderHealth }),
  tts: z.object({ primary: ProviderHealth, fallback: ProviderHealth }),
  cache: z.object({
    enabled: z.boolean(),
    hit_rate: z.number().nullable(),
    size_bytes: z.number().nullable(),
    size_entries: z.number().nullable(),
  }),
  window_minutes: z.number(),
  collected_at: z.string(),
});
export type VoiceConfigSnapshot = z.infer<typeof VoiceConfigSnapshotSchema>;
export type ProviderHealthT = z.infer<typeof ProviderHealth>;

export class VoiceGatewayUnreachableError extends Error {
  constructor(public detail: string) {
    super(detail);
    this.name = 'VoiceGatewayUnreachableError';
    Object.setPrototypeOf(this, VoiceGatewayUnreachableError.prototype);
  }
}

export function useVoiceConfig(token: string | null) {
  return useQuery<VoiceConfigSnapshot>({
    queryKey: ['admin', 'platform', 'voice-config'],
    enabled: token !== null,
    refetchInterval: 30_000,
    retry: false,
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/admin/platform/voice-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 503) {
        const body = await res.json().catch(() => ({})) as { detail?: string };
        throw new VoiceGatewayUnreachableError(body?.detail ?? 'Voice-gateway non raggiungibile');
      }
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`${res.status}: ${body}`);
      }
      return VoiceConfigSnapshotSchema.parse(await res.json());
    },
  });
}
